import { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Loader2, Edit } from 'lucide-react';

import { Pagination, PAGINATION_PAGE_SIZE } from '@/components/Pagination';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useContexts } from '@/hooks/use-contexts';
import ContextWizard from './create';
import { Context, ContextTopic } from '@/types/contexts';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useAuth } from '@/contexts/auth';
import { hasPermission } from '@/utils/permissions';

export default function ContextsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const queryParams = useMemo(() => ({
    offset: (currentPage - 1) * PAGINATION_PAGE_SIZE,
    limit: PAGINATION_PAGE_SIZE,
    q: debouncedSearchTerm || undefined
  }), [currentPage, debouncedSearchTerm]);

  const { contexts, total, loading, refetch } = useContexts(queryParams);
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'edit_content');

  const currentItems = contexts || [];

  const handleWizardSuccess = () => {
    refetch();
    toast({
      title: 'Sucesso',
      description: 'Contexto criado com sucesso!',
      variant: 'default',
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-end mb-6">
        <div className="relative w-full sm:w-auto">
          <Input
            placeholder="Procurar contextos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full sm:w-64"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 stroke-gray-400 w-4 h-4" />
        </div>
        <PermissionGuard action="create_content">
          <Button 
            onClick={() => setIsWizardOpen(true)}
            className="flex items-center gap-2 bg-black text-white hover:bg-gray-900 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            Novo
          </Button>
        </PermissionGuard>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Carregando contextos...</span>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tópicos</TableHead>
              <TableHead>Territórios</TableHead>
              {canEdit && <TableHead className="text-right">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 4 : 3} className="text-center py-8 text-gray-500">
                  Nenhum contexto encontrado
                </TableCell>
              </TableRow>
            ) : (
              currentItems.map((context: Context) => (
                <TableRow key={context.id} className="h-[60px]">
                  <TableCell className="font-medium">{context.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {context.context_topic.slice(0, 2).map((ct: ContextTopic) => (
                        <Badge key={ct.topic_id} variant="secondary" className="text-xs">
                          {ct.topic.name}
                        </Badge>
                      ))}
                      {context.context_topic.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{context.context_topic.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {context.context_territory.slice(0, 2).map((ct) => (
                        <Badge key={ct.territory_id} variant="secondary" className="text-xs">
                          {ct.territory.name}
                        </Badge>
                      ))}
                      {context.context_territory.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{context.context_territory.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <button className="p-1 rounded-md border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {!loading && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          onPageChange={setCurrentPage}
        />
      )}

      <ContextWizard
        open={isWizardOpen}
        onOpenChange={setIsWizardOpen}
        onSuccess={handleWizardSuccess}
      />
    </div>
  );
}
